import { Capacitor } from '@capacitor/core'
import { IonInfiniteScroll } from '@ionic/react'
import { AnimatePresence } from 'framer-motion'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useMyMonitoringGetNotifications } from '@apps-common/apis/my-monitoring/notifications'
import { useGetNotifications } from '@apps-common/apis/portal/notifications'
import { EndOfResults, InfiniteLoader, NoResultsCard, PullRefresh } from '@apps-common/components/common'
import {
  ModalBodyInfiniteScrollable,
  ModalCloseButton,
  ModalContainer,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { NotificationCard } from '@apps-common/components/modals/notifications/NotificationCard'
import { ModalDismiss } from '@apps-common/config/modal'
import { removeAllDeliveredNotifications } from '@apps-common/contexts/NotificationContext'
import { useProjectHook } from '@apps-common/hooks/useProjectHook'
import { useLastViewedNotificationStore } from '@apps-common/stores'
import { INotificationLogModel } from '@apps-common/types/portal/notification'
import { NotificationSkeletonCard } from './NotificationSkeletonCard'

export const NotificationsModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { lastId, setLastId } = useLastViewedNotificationStore()
  const [lastIdRef, setLastIdRef] = useState<number>(0)
  const initialLastIdRef = useRef(lastId || 0)
  const resetLastIdOnNextDataRef = useRef(false)
  const useGet = useProjectHook(useGetNotifications, useMyMonitoringGetNotifications)
  const { isLoading, isError, isFetching, data, refetch, fetchNextPage } = useGet({
    take: 20
  })

  const refreshNotifications = useCallback(async () => {
    resetLastIdOnNextDataRef.current = true
    await refetch()
  }, [refetch])

  useEffect(() => {
    if (data) {
      const reduced: INotificationLogModel[] =
        data?.pages.reduce((acc: INotificationLogModel[], curr: INotificationLogModel[]) => [...acc, ...curr], []) || []
      if (reduced.length > 0) {
        const latestId = reduced[0].id

        setLastIdRef(resetLastIdOnNextDataRef.current ? latestId : initialLastIdRef.current)
        setLastId(latestId)
        resetLastIdOnNextDataRef.current = false
      }
    }
  }, [data])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return
    }

    removeAllDeliveredNotifications().catch((error) => {
      console.error('Failed to clear delivered notifications', error)
    })
  }, [])

  const reducedData = useMemo(() => {
    return data?.pages.reduce((acc: INotificationLogModel[], curr: INotificationLogModel[]) => [...acc, ...curr], []) || []
  }, [data])

  const upToDateIndex = useMemo(() => {
    if (!lastIdRef) {
      return -1
    }

    return reducedData.findIndex((n: INotificationLogModel) => n.id <= lastIdRef)
  }, [lastIdRef, reducedData])

  const loading = isLoading || isError

  return (
    <ModalContainer
      containerClasses=''
      innerClasses='!shadow-md md:!max-w-[420px] !min-h-[calc(100dvh-30px)] !h-[calc(100dvh-30px)] !mx-[1rem] md:!ml-auto md:!mr-[15px]'
      onDismiss={onDismiss}
    >
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader title='Notifications' titleClasses='flex items-center justify-start gap-[10px]' />
          <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} ignoreCtxTheme />
        </div>
      </ModalHeaderContainer>
      <ModalBodyInfiniteScrollable classes='!pt-[90px]'>
        <PullRefresh loading={loading} callback={refreshNotifications} />
        <AnimatePresence>
          <div className='flex flex-col gap-[10px]'>
            {loading && Array.from(Array(8).keys()).map((n) => <NotificationSkeletonCard key={`notification-list-skeleton-${n}`} />)}
            {!loading &&
              reducedData.length > 0 &&
              reducedData.map((n: INotificationLogModel, index: number) => (
                <Fragment key={`notification-list-row-${n.id}`}>
                  {index === upToDateIndex && <EndOfResults key={`notification-list-up-to-date-${n.id}`} show={true} text='Up to date' />}
                  <NotificationCard key={`notification-list-${n.id}`} model={n} lastViewedId={lastIdRef} />
                </Fragment>
              ))}
            {!loading && reducedData.length === 0 && <NoResultsCard>You have no notifications at the moment.</NoResultsCard>}
          </div>
        </AnimatePresence>

        <IonInfiniteScroll
          onIonInfinite={async (ev) => {
            await fetchNextPage()
            ev.target.complete()
          }}
        >
          <InfiniteLoader key='notifications-modal-list-infinite-loader' loading={isFetching} />
        </IonInfiniteScroll>
      </ModalBodyInfiniteScrollable>
    </ModalContainer>
  )
}
